import { useState } from 'react';
import { isAbleToSeeUIElement } from '../../../utils/roles/client-roles';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { pagesConfig, userDetailPages } from '../../../shared/pages-config';
import { useUserSettings } from '../../../utils/use-user-settings';
import UserDetailsLayout from './user-details-layout';
import UserDetailsCard from './user-details-card';
import { Messages } from '../../../utils/message-constants';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import { isFeatureEnabled } from '../../../utils/feature-flags';

import SharedTabCard from '../../../shared/shared-tab-card';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

export default function UserDetailsPage() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, usersTier, isDefault, features } = useUserSettings();

  const location = useLocation();
  const pathNumber = location.pathname.split('/').length;
  const tabsValue =
    pathNumber === 5
      ? location.pathname.split('/')[3]
      : pathNumber > 3
      ? location.pathname.split('/').pop()
      : pagesConfig.generalSettings.link;

  const [detailsExpanded, setDetailsExpanded] = useState(false);
  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: pagesConfig.users.name, path: pagesConfig.users.link },
        { label: pagesConfig.userDetails.name },
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <UserDetailsCard
            userEmail={userEmail}
            detailsExpanded={detailsExpanded}
            setDetailsExpanded={setDetailsExpanded}
          />
        </Box>

        <SharedTabCard sx={{ mt: 1 }}>
          <Tabs
            value={tabsValue}
            variant="scrollable"
            allowScrollButtonsMobile
            scrollButtons={true}
            sx={{
              mx: 1,
              '& .MuiTabs-flexContainer': {
                marginTop: '6px',
              },
              '.MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
            }}
          >
            {userDetailPages.map((page) => {
              if (
                page.requiredFeatureFlag &&
                !isFeatureEnabled(page.requiredFeatureFlag, features)
              ) {
                return null;
              }

              const doesntHavePermission =
                !isAbleToSeeUIElement(userRoles, page.requiredPermission) &&
                page.requiredPermission !== '*';
              const doesntHaveTier = usersTier.rank < page.minimumTier;
              const shouldShowButDisabled = isDefault && doesntHaveTier;
              if (shouldShowButDisabled) {
                return (
                  <Tab
                    key={page.name}
                    value={page.link}
                    component={Link}
                    to={page.link}
                    label={
                      <Tooltip
                        title={Messages.Errors.Tier.Viewing(
                          page.minimumTierDisplayValue
                        )}
                        onClick={(e) => e.preventDefault()}
                        arrow
                      >
                        <span style={{ pointerEvents: 'auto' }}>
                          {page.name}
                        </span>
                      </Tooltip>
                    }
                    icon={<page.icon />}
                    iconPosition="start"
                    sx={{
                      minHeight: '40px',
                      height: '40px',
                      width: 'fit-content',
                    }}
                    disabled
                  />
                );
              }

              return (
                !(doesntHavePermission || doesntHaveTier) && (
                  <Tab
                    key={page.name}
                    value={page.link}
                    component={Link}
                    to={page.link}
                    label={page.name}
                    icon={<page.icon />}
                    iconPosition="start"
                    sx={{
                      minHeight: '40px',
                      height: '40px',
                      width: 'fit-content',
                    }}
                  />
                )
              );
            })}
          </Tabs>
        </SharedTabCard>

        <UserDetailsLayout tabsValue={tabsValue}>
          <Outlet />
        </UserDetailsLayout>
      </Box>
    </SimpleBreadcrumbsPage>
  );
}
